import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, X, Warning, Trophy, Target, Percent, Users, Info,
  ThumbsUp, ThumbsDown, CaretRight, CaretDown, ArrowsClockwise,
} from '@phosphor-icons/react';
import useAuthStore from '../../store/authStore';
import { useTenantPath } from '../../lib/tenantPath';
import { API, inr, fmtDate, PeriodStatusPill, ApprovalPill } from './_shared';

import { confirm } from '../../components/ConfirmDialog';
import { notify } from '../../lib/notify';
import { PageSkeleton } from '../../components/Skeletons';
/**
 * Period detail — 3 modes based on live_status:
 *   1. commitment_open   → shows commitments so far + link back to /commit
 *   2. in_progress       → commitments frozen, VP+ sees "Close & Compute" CTA
 *   3. closed            → results table with Won/Missed + achievement %
 *
 * VP+ additionally sees an "approvals inbox" strip when any commitment has
 * pending under-commit approval — one-click approve/reject inline.
 */
export default function PeriodDetail() {
  const { periodId } = useParams();
  const nav = useNavigate();
  const tp = useTenantPath();
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // iter114 — backend now returns `viewer_can_manage` so in-charges see
  // the Close/Approve controls without duplicating the role math here.
  // Fall back to local role check when the flag is missing (older cached
  // response during transition).
  const localIsVpPlus = ['Vice President', 'President'].includes(user?.role);
  const canManage = data?.viewer_can_manage ?? localIsVpPlus;
  // iter110/113 — track which sub-table (if any) is open per row.
  // Value keyed as `${scope}-${user_id}`; each row can have one open bucket:
  //   'all'    → show every committed lead (commitment table)
  //   'won'    → show only won leads (results table)
  //   'missed' → show only missed leads (results table)
  // Clicking the same bucket again closes it. Clicking a different bucket
  // on the same row swaps to the new filter.
  const [openBucket, setOpenBucket] = useState({}); // {[user_id]: 'all'|'won'|'missed'|null}
  const toggleBucket = (userId, bucket) =>
    setOpenBucket((s) => ({ ...s, [userId]: s[userId] === bucket ? null : bucket }));

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reviews/periods/${periodId}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periodId]);

  const closePeriod = async () => {
    if (!await confirm({ message: 'Close this period and compute results? This cannot be undone.' })) return;
    setBusy(true);
    try {
      await axios.post(`${API}/reviews/periods/${periodId}/close`);
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Close failed');
    } finally { setBusy(false); }
  };

  const decideApproval = async (commitmentId, action) => {
    const reason = window.prompt(`${action === 'approve' ? 'Approve' : 'Reject'} — reason (optional):`) ?? '';
    setBusy(true);
    try {
      await axios.post(`${API}/reviews/commitments/${commitmentId}/approve`, { action, reason });
      await load();
    } catch (e) {
      notify(e.response?.data?.detail || 'Decision failed');
    } finally { setBusy(false); }
  };

  if (loading) return <PageSkeleton />;
  if (!data) return <div className="p-6 text-red-600">Period not found</div>;

  const { period, commitments, results } = data;
  const live = period.live_status;
  const isClosed = live === 'closed';
  const pendingApprovals = (commitments || []).filter((c) => c.approval_status === 'pending');

  // Aggregate stats (for closed periods)
  const totals = (results || []).reduce((acc, r) => ({
    target: acc.target + (r.target_value || 0),
    committed: acc.committed + (r.committed_value || 0),
    won: acc.won + (r.won_value || 0),
  }), { target: 0, committed: 0, won: 0 });
  const overallAchievement = totals.target ? (totals.won / totals.target * 100) : 0;
  const overallAccuracy = totals.committed ? (totals.won / totals.committed * 100) : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="period-detail">
      <button onClick={() => nav(-1)} className="text-sm text-[#6D8E87] hover:text-[#00a69c] inline-flex items-center gap-1 mb-4" data-testid="back-btn">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold">{period.cycle_title}</div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="period-title">
            Review — {fmtDate(period.review_date)}
          </h1>
          <div className="text-xs text-[#6D8E87] mt-1">
            Period: {fmtDate(period.period_start)} → {fmtDate(period.period_end)}
            {period.closed_at && <> · closed {fmtDate(period.closed_at)} by {period.closed_by}</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PeriodStatusPill status={live} />
          {canManage && !isClosed && live !== 'upcoming' && (
            <button onClick={closePeriod} disabled={busy}
              className="btn-primary text-xs px-3 py-1.5" data-testid="close-period-btn">
              Close & Compute Review
            </button>
          )}
          {live === 'commitment_open' && (
            <Link to={tp(`/reviews/commit/${period.id}`)} className="btn-primary text-xs px-3 py-1.5" data-testid="jump-to-commit-btn">
              Commit / Update →
            </Link>
          )}
        </div>
      </div>

      {/* Closed → results view */}
      {isClosed && (
        <>
          {/* Overall summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="results-summary">
            <ResultCard icon={Target} label="Total Target" value={inr(totals.target)} />
            <ResultCard icon={Users}  label="Committed"   value={inr(totals.committed)} />
            <ResultCard icon={Trophy} label="Won"         value={inr(totals.won)} tone="revenue" />
            <ResultCard icon={Percent} label="Achievement" value={`${overallAchievement.toFixed(1)}%`}
              tone={overallAchievement >= 100 ? 'revenue' : overallAchievement >= 50 ? 'warning' : 'danger'}
              sub={`Accuracy ${overallAccuracy.toFixed(1)}%`} />
          </div>

          {/* Per-member table */}
          <div className="medical-card overflow-x-auto" data-testid="results-table">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-[#F4F9F7]">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Member</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Target</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Committed</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Won</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Missed</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Achievement</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {(results || []).map((r) => {
                  const bucket = openBucket[r.user_id];
                  const allLeads = r.leads || [];
                  const wonLeads = allLeads.filter((l) => l.outcome === 'won');
                  const missedLeads = allLeads.filter((l) => l.outcome === 'missed');
                  const visibleLeads = bucket === 'won' ? wonLeads
                                     : bucket === 'missed' ? missedLeads
                                     : bucket === 'all' ? allLeads
                                     : null;
                  return (
                  <React.Fragment key={r.id}>
                  <tr className="border-t border-[#D5E8E4] hover:bg-[#F4F9F7]" data-testid={`result-row-${r.user_id}`}>
                    <td className="px-3 py-2">
                      <div className="text-sm font-medium text-[#052A23]">{r.user_name}</div>
                      <div className="text-[10px] text-[#6D8E87]">{r.user_role}</div>
                      {r.inadequate_flag && (
                        <span className="mt-1 inline-block text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded border border-amber-200">
                          Inadequate commitment
                        </span>
                      )}
                      {r.repeat_lead_count > 0 && (
                        <span className="mt-1 ml-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded border border-amber-200" data-testid={`result-repeat-count-${r.user_id}`}>
                          <ArrowsClockwise size={9} weight="bold" /> {r.repeat_lead_count} repeat
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{inr(r.target_value)}</td>
                    <td className="px-3 py-2 text-right">
                      <ValueDrilldown
                        value={inr(r.committed_value)}
                        count={allLeads.length}
                        active={bucket === 'all'}
                        disabled={allLeads.length === 0}
                        onClick={() => toggleBucket(r.user_id, 'all')}
                        testid={`result-committed-${r.user_id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ValueDrilldown
                        value={inr(r.won_value)}
                        count={wonLeads.length}
                        active={bucket === 'won'}
                        disabled={wonLeads.length === 0}
                        tone="emerald"
                        onClick={() => toggleBucket(r.user_id, 'won')}
                        testid={`result-won-${r.user_id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ValueDrilldown
                        value={inr(r.missed_value)}
                        count={missedLeads.length}
                        active={bucket === 'missed'}
                        disabled={missedLeads.length === 0}
                        tone="red"
                        onClick={() => toggleBucket(r.user_id, 'missed')}
                        testid={`result-missed-${r.user_id}`}
                      />
                    </td>
                    <td className={`px-3 py-2 text-right font-bold ${r.achievement_pct >= 100 ? 'text-emerald-600' : r.achievement_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {r.achievement_pct}%
                    </td>
                    <td className="px-3 py-2 text-right text-[#3B5A54]">{r.accuracy_pct}%</td>
                  </tr>
                  {visibleLeads && visibleLeads.length > 0 && (
                    <tr className="bg-[#F9FCFB]" data-testid={`result-leads-${r.user_id}`}>
                      <td colSpan={7} className="px-4 py-3">
                        <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold mb-2">
                          {bucket === 'won' ? 'Won leads' : bucket === 'missed' ? 'Missed leads' : 'All committed leads'} — {visibleLeads.length}
                        </div>
                        <LeadsSubTable rows={visibleLeads} showOutcome={true} tp={tp} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );})}
                {(results || []).length === 0 && (
                  <tr><td colSpan={7} className="p-6 text-center text-sm text-[#6D8E87]">No results computed yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pre-close: commitments view */}
      {!isClosed && (
        <>
          {canManage && pendingApprovals.length > 0 && (
            <section className="medical-card p-4 mb-4 border-l-4 border-amber-500" data-testid="approvals-inbox">
              <h3 className="text-sm font-bold text-[#92400E] mb-2 uppercase tracking-widest flex items-center gap-1.5">
                <Warning size={14} weight="fill" /> Under-commit approvals awaiting you ({pendingApprovals.length})
              </h3>
              <div className="space-y-2">
                {pendingApprovals.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 p-2 bg-amber-50 rounded" data-testid={`approval-row-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#052A23]">{c.user_name} <span className="text-xs text-[#6D8E87]">— {c.user_role}</span></div>
                      <div className="text-xs text-[#3B5A54]">
                        Committed <b>{inr(c.committed_value)}</b> vs target <b>{inr(c.target_value)}</b> — <b className="text-amber-700">{c.coverage_pct}%</b>
                      </div>
                      {c.under_commit_reason && (
                        <div className="text-[11px] text-[#6D8E87] italic mt-1 truncate">&ldquo;{c.under_commit_reason}&rdquo;</div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => decideApproval(c.id, 'approve')} disabled={busy}
                        aria-label={`Approve commitment for ${c.user_name}`}
                        className="p-1.5 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`approve-btn-${c.id}`} title="Approve">
                        <ThumbsUp size={14} weight="bold" />
                      </button>
                      <button onClick={() => decideApproval(c.id, 'reject')} disabled={busy}
                        aria-label={`Reject commitment for ${c.user_name}`}
                        className="p-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-btn-${c.id}`} title="Reject">
                        <ThumbsDown size={14} weight="bold" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="medical-card overflow-x-auto" data-testid="commitments-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#F4F9F7]">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Member</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Target</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Committed</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Coverage</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Leads</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Status</th>
                </tr>
              </thead>
              <tbody>
                {(period.member_targets || []).map((m) => {
                  const c = commitments?.find((x) => x.user_id === m.user_id);
                  const leads = (c && c.leads) || [];
                  const bucket = openBucket[m.user_id];
                  const isOpen = bucket === 'all';
                  const canExpand = leads.length > 0;
                  return (
                    <React.Fragment key={m.user_id}>
                    <tr className="border-t border-[#D5E8E4]" data-testid={`commit-row-${m.user_id}`}>
                      <td className="px-3 py-2">
                        <div className="text-sm font-medium text-[#052A23]">{m.user_name}</div>
                        <div className="text-[10px] text-[#6D8E87]">{m.user_role}</div>
                      </td>
                      <td className="px-3 py-2 text-right">{inr(m.target_value)}</td>
                      <td className="px-3 py-2 text-right">
                        {c ? (
                          <ValueDrilldown
                            value={inr(c.committed_value)}
                            count={leads.length}
                            active={isOpen}
                            disabled={!canExpand}
                            onClick={() => toggleBucket(m.user_id, 'all')}
                            testid={`commit-committed-${m.user_id}`}
                          />
                        ) : <span className="text-[#D5E8E4]">not yet</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-bold ${c && c.coverage_pct >= 100 ? 'text-emerald-600' : c ? 'text-amber-600' : 'text-[#D5E8E4]'}`}>
                        {c ? `${c.coverage_pct}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#3B5A54]">
                        {canExpand ? (
                          <span>
                            <b>{leads.length}</b> lead{leads.length !== 1 ? 's' : ''}
                            {c.repeat_lead_count > 0 && (
                              <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded" data-testid={`commit-repeat-count-${m.user_id}`}>
                                <ArrowsClockwise size={9} weight="bold" /> {c.repeat_lead_count} repeat
                              </span>
                            )}
                          </span>
                        ) : <span className="text-[#D5E8E4]">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        {c ? (
                          <span className="text-xs">
                            {c.is_under_committed && <span className="mr-1 text-amber-700 font-bold text-[10px] uppercase">UNDER</span>}
                            submitted {fmtDate(c.submitted_at)}
                            <ApprovalPill status={c.approval_status} />
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">pending</span>
                        )}
                      </td>
                    </tr>
                    {isOpen && canExpand && (
                      <tr className="bg-[#F9FCFB]" data-testid={`commit-leads-${m.user_id}`}>
                        <td colSpan={6} className="px-4 py-3">
                          <LeadsSubTable rows={leads} showOutcome={false} tp={tp} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mt-4 text-xs text-[#6D8E87] flex items-center gap-1">
            <Info size={12} /> Results (Won/Missed) will compute automatically when this period is closed.
          </p>
        </>
      )}
    </div>
  );
}

function ResultCard({ icon: Icon, label, value, sub, tone = 'default' }) {
  const border = tone === 'revenue' ? 'border-l-emerald-500' :
                 tone === 'warning' ? 'border-l-amber-500' :
                 tone === 'danger'  ? 'border-l-red-500' : 'border-l-[#00a69c]';
  return (
    <div className={`medical-card p-4 border-l-4 ${border}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold flex items-center gap-1"><Icon size={12} /> {label}</div>
      <div className="text-2xl font-black text-[#052A23] mt-1" style={{ fontFamily: 'Outfit' }}>{value}</div>
      {sub && <div className="text-[11px] text-[#6D8E87] mt-1">{sub}</div>}
    </div>
  );
}

// iter110/113 — Lead-level sub-table shown under an expanded commitment/result
// row. Shows customer, quote #, value, expected close, repeat pill, and
// (post-close) won/missed outcome + LORD reason. Customer name is a link to
// the full Lead detail page for one-click drill-down.
function LeadsSubTable({ rows, showOutcome, tp }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-[#6D8E87] italic p-2">No lead detail available.</div>;
  }
  return (
    <table className="w-full text-xs bg-white rounded border border-[#E2ECEA]">
      <thead className="bg-[#F4F9F7]">
        <tr>
          <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3B5A54]">Customer</th>
          <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3B5A54]">Quote #</th>
          <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3B5A54]">Product</th>
          <th className="text-right px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3B5A54]">Value</th>
          <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3B5A54]">Expected close</th>
          <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3B5A54]">Repeat?</th>
          {showOutcome && <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3B5A54]">Outcome</th>}
          {showOutcome && <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#3B5A54]">Close reason</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((l) => {
          // iter113 — Customer name deep-links to Lead detail when we have a
          // lead_id (backend fills it for every hydrated row). tp() prefixes
          // the tenant slug so the URL is always correct.
          const canLink = !!(tp && l.lead_id);
          const customerCell = (
            <>
              <div className="text-xs font-medium text-[#052A23]">{l.customer_name || '—'}</div>
              {l.contact_name && <div className="text-[10px] text-[#6D8E87]">{l.contact_name}</div>}
            </>
          );
          return (
            <tr key={l.quote_id} className="border-t border-[#E2ECEA] hover:bg-[#F4F9F7]" data-testid={`lead-row-${l.quote_id}`}>
              <td className="px-3 py-1.5">
                {canLink ? (
                  <Link to={tp(`/leads/${l.lead_id}`)} className="hover:underline text-[#00857e]" data-testid={`lead-link-${l.quote_id}`}>
                    {customerCell}
                  </Link>
                ) : customerCell}
              </td>
              <td className="px-3 py-1.5 text-[#3B5A54]">{l.quotation_number || '—'}</td>
              <td className="px-3 py-1.5 text-[#3B5A54]">{l.product_summary || '—'}</td>
              <td className="px-3 py-1.5 text-right font-bold text-[#052A23]">{inr(l.quote_value)}</td>
              <td className="px-3 py-1.5 text-[#3B5A54]">{l.expected_closing_date ? fmtDate(l.expected_closing_date) : '—'}</td>
              <td className="px-3 py-1.5">
                <RepeatPill count={l.repeat_count} />
              </td>
              {showOutcome && (
                <td className="px-3 py-1.5">
                  <OutcomePill outcome={l.outcome} />
                </td>
              )}
              {showOutcome && (
                <td className="px-3 py-1.5 text-[11px] text-[#3B5A54] max-w-[220px]">
                  {l.outcome === 'missed'
                    ? (l.lost_reason
                        ? <span title={l.lost_reason} className="line-clamp-2">{l.lost_reason}</span>
                        : <span className="text-[#94A3B8] italic">still open — no LORD yet</span>)
                    : <span className="text-[#D5E8E4]">—</span>}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// iter113 — Clickable numeric cell used in commitments/results tables. Shows
// the INR value + tiny lead-count chip; wraps the whole thing in a button so
// the user can click the value itself to drop the leads. `active` renders the
// caret rotated down + a subtle highlight so it doubles as an "expanded"
// indicator. Falls back to a plain <span> when disabled (no leads to drill).
function ValueDrilldown({ value, count, active, disabled, tone, onClick, testid }) {
  const toneCls = tone === 'emerald' ? 'text-emerald-700' : tone === 'red' ? 'text-red-600' : 'text-[#052A23]';
  if (disabled || !count) {
    return <span className={`font-bold ${toneCls}`}>{value}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group inline-flex items-center gap-1 font-bold rounded px-1.5 py-0.5 -mr-1.5 hover:bg-[#F4F9F7] transition ${active ? 'bg-[#E2ECEA] ring-1 ring-[#00a69c]' : ''} ${toneCls}`}
      data-testid={testid}
      aria-expanded={active}
      title={`${count} lead${count === 1 ? '' : 's'} — click to ${active ? 'hide' : 'view'}`}
    >
      {value}
      <span className="text-[9px] font-bold uppercase tracking-widest text-[#6D8E87] group-hover:text-[#00a69c]">
        · {count}
      </span>
      {active
        ? <CaretDown size={11} weight="bold" className="text-[#00a69c]" />
        : <CaretRight size={11} weight="bold" className="text-[#94A3B8] group-hover:text-[#00a69c]" />}
    </button>
  );
}

function RepeatPill({ count }) {
  const n = Number(count) || 1;
  if (n <= 1) return <span className="text-[10px] text-[#6D8E87]">new</span>;
  // iter110 — ordinal suffix so "3rd" / "4th" / "21st" etc. reads naturally.
  const ord = (x) => {
    const s = x % 100;
    if (s >= 11 && s <= 13) return `${x}th`;
    return `${x}${['th','st','nd','rd','th','th','th','th','th','th'][x % 10]}`;
  };
  if (n === 2) return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded border border-amber-200" data-testid="repeat-pill-amber">
      <ArrowsClockwise size={9} weight="bold" /> 2nd week
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800 px-1.5 py-0.5 rounded border border-red-200" data-testid="repeat-pill-red">
      <ArrowsClockwise size={9} weight="bold" /> {ord(n)} week
    </span>
  );
}

function OutcomePill({ outcome }) {
  if (outcome === 'won') return <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">Won</span>;
  if (outcome === 'missed') return <span className="text-[10px] font-bold uppercase text-red-700 bg-red-50 px-1.5 py-0.5 rounded border border-red-200">Missed</span>;
  return <span className="text-[10px] font-bold uppercase text-slate-600 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200">Open</span>;
}
